import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import Loader from "@components/Loader/Loader";
import PatientInfoForm from "@components/PatientInfoForm/PatientInfoForm";
import { FieldValues, useForm } from "react-hook-form";
import { PatientFormType, patientToFormType } from "@utils/patientFormHelper";
import { setMessage } from "@store/snackbarSlice";
import { useUpdatePatientMutation } from "@store/patientApi";
import HomeForm from "@components/Home/HomeForm/HomeForm";
import HomeFormButtons from "@components/Home/HomeFormButtons/HomeFormButtons";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import TextIconButton from "@components/TextIconButton";

const PersonalInfo: FC = () => {
  const { t } = useTranslation();
  const patient = useSelector((state: RootState) => state.home.selectedPatient);
  const [updatePatient] = useUpdatePatientMutation();
  const dispatch = useDispatch();
  const [avatarFile, setAvatarFile] = useState<File | undefined | null>();

  const form = useForm<PatientFormType>({
    mode: "onChange",
    criteriaMode: "all",
  });
  const {
    reset,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting },
  } = form;

  useEffect(() => {
    if (patient) {
      reset(patientToFormType(patient));
    }
  }, [patient, reset]);

  const save = async (values: FieldValues) => {
    try {
      const { data, error } = await updatePatient({
        ...values,
        id: patient?.id,
        avatar: avatarFile as File,
      });
      if (!data || error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        dispatch(
          setMessage({
            content: t(`personalInfo.updateSuccessMessage`),
            severity: "success",
          }),
        );
      }
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
  };

  return (
    <Box display="flex" flexDirection="column">
      {!patient ? (
        <Loader />
      ) : (
        <form
          onSubmit={(e) => {
            clearErrors();
            return handleSubmit(save)(e);
          }}
        >
          <HomeForm>
            <PatientInfoForm form={form} onAvatarChange={setAvatarFile} />
          </HomeForm>

          <HomeFormButtons>
            <TextIconButton
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              size="large"
              icon={SaveOutlinedIcon}
              text={t("personalInfo.saveButton")}
            />
          </HomeFormButtons>
        </form>
      )}
    </Box>
  );
};

export default PersonalInfo;
